// Project-scoped onboarding hub. Left rail lists won leads for the currently
// selected project (Onboarding / Onboarded tabs); right pane shows the
// selected lead's checklist with completion progress and a Mark as Onboarded
// action. Backend fields are title/order/completedAt (not text/day/completed).
//
// Scoping: reads projectId from SelectedProjectProvider (project dropdown in
// the page header). When no project is selected, shows an empty-state
// prompting the user to pick one.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Search,
  CheckCircle2,
  Circle,
  Plus,
  Trash2,
  Trophy,
  ShieldCheck,
  Loader2,
  ChevronRight,
  TrendingUp,
  FolderOpen,
} from "lucide-react";
import toast from "react-hot-toast";
import { useLeads, useLeadOnboarding, useCreateOnboardingItem, useUpdateOnboardingItem, useDeleteOnboardingItem, useMarkLeadOnboarded, useUnmarkLeadOnboarded } from "../hooks/useLeads";
import { useSelectedProject } from "../contexts/SelectedProjectProvider";
import PageHeader from "../organisms/PageHeader";
import Button from "../atoms/Button";
import Badge from "../atoms/Badge";

function formatErr(err) {
  const data = err?.response?.data;
  if (typeof data === "string") return data;
  if (data?.error) return typeof data.error === "string" ? data.error : data.error.message;
  return err?.message || "Request failed";
}

function fmtDate(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch { return ""; }
}

export default function Onboarding() {
  const { projectId, project } = useSelectedProject();
  const [activeTab, setActiveTab] = useState("onboarding"); // 'onboarding' | 'onboarded'
  const [search, setSearch] = useState("");
  const [selectedLeadId, setSelectedLeadId] = useState(null);

  // Fetch won leads for current project. useLeads is project-scoped via the
  // SelectedProjectProvider already, so no need to pass projectId explicitly.
  const leadsQuery = useLeads({ status: "won", limit: 200, sort: "wonAt", order: "desc" });
  const allWon = useMemo(() => leadsQuery.data?.items || [], [leadsQuery.data]);

  const onboardingLeads = useMemo(() => allWon.filter((l) => !l.onboardedAt), [allWon]);
  const onboardedLeads = useMemo(() => allWon.filter((l) => l.onboardedAt), [allWon]);
  const currentLeads = activeTab === "onboarding" ? onboardingLeads : onboardedLeads;

  const filteredLeads = useMemo(() => {
    if (!search.trim()) return currentLeads;
    const q = search.toLowerCase();
    return currentLeads.filter((l) =>
      (l.title || "").toLowerCase().includes(q) ||
      (l.brandName || "").toLowerCase().includes(q)
    );
  }, [currentLeads, search]);

  // Reset selection when tab or project changes.
  useEffect(() => { setSelectedLeadId(null); }, [activeTab, projectId]);

  // Auto-select the first lead in the filtered set if nothing is selected yet.
  useEffect(() => {
    if (!selectedLeadId && filteredLeads.length > 0) setSelectedLeadId(filteredLeads[0]._id);
  }, [filteredLeads, selectedLeadId]);

  const selectedLead = useMemo(
    () => allWon.find((l) => l._id === selectedLeadId) || null,
    [allWon, selectedLeadId]
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, background: "var(--color-bg-base)" }}>
      <PageHeader
        title="Onboarding"
        subtitle="Won deals and their onboarding checklists"
      />
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
      <Sidebar
        project={project}
        projectId={projectId}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onboardingCount={onboardingLeads.length}
        onboardedCount={onboardedLeads.length}
        search={search}
        setSearch={setSearch}
        leads={filteredLeads}
        loading={leadsQuery.isLoading}
        selectedLeadId={selectedLeadId}
        onSelect={setSelectedLeadId}
      />
      <MainPane lead={selectedLead} activeTab={activeTab} hasProject={!!projectId} />
      </div>
    </div>
  );
}

function Sidebar({
  project, projectId, activeTab, setActiveTab, onboardingCount, onboardedCount,
  search, setSearch, leads, loading, selectedLeadId, onSelect,
}) {
  return (
    <div style={{
      width: 320, flexShrink: 0,
      borderRight: "1px solid var(--color-border-subtle)",
      background: "var(--color-bg-surface)",
      display: "flex", flexDirection: "column",
    }}>
      <div style={{ padding: "14px 14px 12px", borderBottom: "1px solid var(--color-border-subtle)", display: "flex", flexDirection: "column", gap: 8 }}>
        {/* Flush at the block's own 14px inset — no extra local padding — so
            this lines up with the pill/search box edges and the row text
            below, instead of sitting 8px further right than everything else. */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--color-text-muted)" }}>
          <FolderOpen size={13} />
          {project?.name || (projectId ? "Project" : "All projects")}
        </div>

        <div className="tg-filterbar">
          <TabPill
            active={activeTab === "onboarding"}
            onClick={() => setActiveTab("onboarding")}
            icon={<Trophy size={13} />}
            label="Onboarding"
            count={onboardingCount}
          />
          <TabPill
            active={activeTab === "onboarded"}
            onClick={() => setActiveTab("onboarded")}
            icon={<ShieldCheck size={13} />}
            label="Onboarded"
            count={onboardedCount}
          />
        </div>

        <label className="tg-tool tg-tool-search" style={{ maxWidth: "none" }}>
          <Search size={13} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={activeTab === "onboarding" ? "Search won leads…" : "Search onboarded leads…"}
            style={{ width: "100%" }}
          />
        </label>
      </div>

      <div style={{ flex: 1, overflowY: "auto" }}>
        {loading && (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
            <Loader2 size={18} className="animate-spin" style={{ color: "var(--crm-primary)" }} />
          </div>
        )}
        {!loading && leads.length === 0 && (
          <EmptyRail activeTab={activeTab} />
        )}
        {leads.map((lead) => (
          <LeadRow
            key={lead._id}
            lead={lead}
            selected={selectedLeadId === lead._id}
            activeTab={activeTab}
            onClick={() => onSelect(lead._id)}
          />
        ))}
      </div>
    </div>
  );
}

function TabPill({ active, onClick, icon, label, count }) {
  return (
    <button
      onClick={onClick}
      className="tg-tool"
      style={active ? { background: "var(--color-hover-bg)", color: "var(--color-text-primary)", fontWeight: 500 } : undefined}
    >
      {icon}
      {label}
      <span style={{
        fontSize: 11, fontWeight: 500, lineHeight: "16px",
        padding: "0 5px", borderRadius: 4,
        background: "var(--color-bg-muted)",
        color: "var(--color-text-secondary)",
        fontVariantNumeric: "tabular-nums",
      }}>{count}</span>
    </button>
  );
}

function LeadRow({ lead, selected, activeTab, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        textAlign: "left",
        padding: "9px 14px",
        background: selected ? "var(--color-bg-muted)" : "transparent",
        border: "none",
        borderLeft: `2px solid ${selected ? "var(--crm-primary)" : "transparent"}`,
        cursor: "pointer",
        display: "flex", alignItems: "center", gap: 8,
        transition: "background 120ms",
      }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = "var(--color-hover-bg)"; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = "transparent"; }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: selected ? 500 : 400,
          color: "var(--color-text-primary)",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>{lead.title}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2, flexWrap: "wrap", fontSize: 12, color: "var(--color-text-muted)" }}>
          {lead.closedFromPipelineName && (
            <span style={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {lead.closedFromPipelineName}
            </span>
          )}
          {activeTab === "onboarded" && lead.onboardedAt && <span>onboarded {fmtDate(lead.onboardedAt)}</span>}
          {activeTab === "onboarding" && lead.wonAt && <span>won {fmtDate(lead.wonAt)}</span>}
        </div>
      </div>
      <ChevronRight size={14} style={{ color: "var(--color-icon)", opacity: selected ? 1 : 0.35 }} />
    </button>
  );
}

function EmptyRail({ activeTab }) {
  const Icon = activeTab === "onboarded" ? ShieldCheck : Trophy;
  return (
    <div style={{ padding: 32, textAlign: "center", color: "var(--color-text-secondary)" }}>
      <div style={{
        width: 36, height: 36, margin: "0 auto 8px",
        borderRadius: 8, background: "var(--color-bg-muted)",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        color: "var(--color-icon)",
      }}>
        <Icon size={16} />
      </div>
      <div style={{ fontSize: 13 }}>
        {activeTab === "onboarded" ? "No onboarded leads yet" : "No leads in onboarding"}
      </div>
    </div>
  );
}

function MainPane({ lead, activeTab, hasProject }) {
  if (!hasProject) {
    return (
      <BigEmpty
        icon={<FolderOpen size={22} />}
        title="Select a project"
        body="Use the project dropdown at the top of the CRM to scope onboarding to a project."
      />
    );
  }
  if (!lead) {
    return (
      <BigEmpty
        icon={activeTab === "onboarded" ? <ShieldCheck size={22} /> : <Trophy size={22} />}
        title={activeTab === "onboarded" ? "Onboarded accounts" : "Onboarding hub"}
        body={
          activeTab === "onboarded"
            ? "Select an onboarded lead to view its completed checklist."
            : "Select a won lead to manage its onboarding checklist."
        }
      />
    );
  }
  return <LeadOnboardingPanel lead={lead} />;
}

function BigEmpty({ icon, title, body }) {
  return (
    <div style={{
      flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
      padding: 40, textAlign: "center", color: "var(--color-text-secondary)",
    }}>
      <div>
        <div style={{
          width: 48, height: 48, margin: "0 auto 12px",
          borderRadius: 8, background: "var(--color-bg-surface)",
          border: "1px solid var(--color-border-subtle)",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          color: "var(--color-icon)",
        }}>{icon}</div>
        <div style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)" }}>{title}</div>
        <div style={{ fontSize: 13, marginTop: 4, maxWidth: 340 }}>{body}</div>
      </div>
    </div>
  );
}

function LeadOnboardingPanel({ lead }) {
  const leadId = lead._id;
  const { data, isLoading } = useLeadOnboarding(leadId);
  const items = data?.items || [];

  const createItem = useCreateOnboardingItem(leadId);
  const updateItem = useUpdateOnboardingItem(leadId);
  const deleteItem = useDeleteOnboardingItem(leadId);
  const markOnboarded = useMarkLeadOnboarded(leadId);
  const unmarkOnboarded = useUnmarkLeadOnboarded(leadId);

  const completed = items.filter((i) => i.completedAt).length;
  const total = items.length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  const isOnboarded = !!lead.onboardedAt;
  // Button stays disabled below 100% so users can't accidentally mark a lead
  // onboarded with pending tasks. Need at least one task in the list, too —
  // an empty checklist trivially has 0/0 = 0%.
  const canMarkOnboarded = !isOnboarded && total > 0 && pct === 100;
  const markBlockedReason = total === 0
    ? "Add at least one onboarding task first"
    : pct < 100
      ? `Complete all tasks first (${total - completed} remaining)`
      : "";

  // Track which specific item's mutation is in-flight so only that card shows
  // a loader — not every card (which would flash a global spinner and look
  // like the list is "shuffling").
  const [togglingId, setTogglingId] = useState(null);
  const handleToggle = (item) => {
    setTogglingId(item._id);
    updateItem.mutate(
      { id: item._id, patch: { completed: !item.completedAt } },
      {
        onError: (err) => toast.error(formatErr(err)),
        onSettled: () => setTogglingId((cur) => (cur === item._id ? null : cur)),
      }
    );
  };

  const handleAdd = () => {
    createItem.mutate(
      { title: "New onboarding task" },
      { onError: (err) => toast.error(formatErr(err)) }
    );
  };

  const handleDelete = (itemId) => {
    deleteItem.mutate(itemId, {
      onSuccess: () => toast.success("Task removed"),
      onError: (err) => toast.error(formatErr(err)),
    });
  };

  const handleMarkOnboarded = () => {
    markOnboarded.mutate(undefined, {
      onSuccess: () => toast.success("Marked as onboarded"),
      onError: (err) => toast.error(formatErr(err)),
    });
  };

  const handleUndoOnboarded = () => {
    unmarkOnboarded.mutate(undefined, {
      onSuccess: () => toast.success("Moved back to onboarding"),
      onError: (err) => toast.error(formatErr(err)),
    });
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
      {/* Header */}
      <div style={{
        background: "var(--color-bg-surface)",
        border: "1px solid var(--color-border-subtle)",
        borderRadius: 8, padding: "16px 20px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 20, flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 8,
            background: isOnboarded ? "var(--crm-success-soft)" : "var(--crm-primary-soft)",
            color: isOnboarded ? "var(--crm-success)" : "var(--crm-primary)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            {isOnboarded ? <ShieldCheck size={18} /> : <TrendingUp size={18} />}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <h1 style={{ fontSize: 17, fontWeight: 600, letterSpacing: "-.01em", margin: 0, color: "var(--color-text-primary)" }}>
                {lead.title}
              </h1>
              <Badge tone={isOnboarded ? "success" : "primary"} dot>
                {isOnboarded ? "Onboarded" : "Won"}
              </Badge>
              {lead.closedFromPipelineName && <Badge>{lead.closedFromPipelineName}</Badge>}
            </div>
            <div style={{ marginTop: 2, fontSize: 12, color: "var(--color-text-muted)" }}>
              {isOnboarded
                ? `Onboarded ${fmtDate(lead.onboardedAt)}`
                : lead.wonAt
                  ? `Won ${fmtDate(lead.wonAt)}`
                  : "—"}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 16, flexShrink: 0 }}>
          {/* A labelled bar beats a 38px percentage: the remaining task count is
              what the user acts on, and it stays readable at 0%. */}
          <div style={{ minWidth: 150 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 5 }}>
              <span style={{ color: "var(--color-text-muted)" }}>{completed} of {total} tasks</span>
              <span style={{ color: "var(--color-text-primary)", fontVariantNumeric: "tabular-nums" }}>{pct}%</span>
            </div>
            <div style={{ height: 4, borderRadius: 2, background: "var(--color-bg-muted)", overflow: "hidden" }}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ type: "spring", stiffness: 80, damping: 18 }}
                style={{ height: "100%", background: pct === 100 ? "var(--crm-success)" : "var(--crm-primary)" }}
              />
            </div>
          </div>
          {isOnboarded ? (
            <Button
              variant="secondary" size="sm"
              onClick={handleUndoOnboarded}
              disabled={unmarkOnboarded.isPending}
              leftIcon={unmarkOnboarded.isPending ? <Loader2 size={13} className="animate-spin" /> : <Trophy size={13} />}
            >
              Move to onboarding
            </Button>
          ) : (
            <Button
              variant="primary" size="sm"
              onClick={handleMarkOnboarded}
              disabled={!canMarkOnboarded || markOnboarded.isPending}
              title={canMarkOnboarded ? "Mark this lead as onboarded" : markBlockedReason}
              leftIcon={markOnboarded.isPending ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />}
            >
              Mark as onboarded
            </Button>
          )}
        </div>
      </div>

      {/* Tasks grid */}
      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
          <Loader2 size={22} className="animate-spin" style={{ color: "var(--crm-primary)" }} />
        </div>
      ) : (
        <>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, margin: "24px 0 12px" }}>
          <h2 style={{ margin: 0, fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>
            Checklist
            <span style={{ marginLeft: 8, fontWeight: 400, color: "var(--color-text-muted)", fontVariantNumeric: "tabular-nums" }}>{total}</span>
          </h2>
          {items.length > 0 && (
            <Button
              variant="secondary" size="sm" onClick={handleAdd} disabled={createItem.isPending}
              leftIcon={createItem.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            >
              {createItem.isPending ? "Adding…" : "Add task"}
            </Button>
          )}
        </div>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gap: 12,
        }}>
          {/* AnimatePresence removed — it was driving items to mount/unmount on
              every refetch, which read as "shuffle". Items are stable now;
              border-color flip is the only visible change on toggle. */}
          {items.map((item) => (
            <TaskCard
              key={item._id}
              item={item}
              onToggle={() => handleToggle(item)}
              onDelete={() => handleDelete(item._id)}
              onUpdate={(patch) => updateItem.mutate({ id: item._id, patch }, { onError: (err) => toast.error(formatErr(err)) })}
              togglePending={togglingId === item._id}
              deletePending={deleteItem.isPending}
            />
          ))}

          {items.length === 0 && (
            <div style={{
              gridColumn: "1 / -1",
              padding: "48px 24px", textAlign: "center",
              border: "1px dashed var(--color-border-subtle)", borderRadius: 8,
              background: "var(--color-bg-surface)",
            }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)" }}>No onboarding tasks yet</div>
              <div style={{ fontSize: 13, color: "var(--color-text-secondary)", margin: "4px 0 16px" }}>
                Add the steps this account needs before it goes live.
              </div>
              <Button
                variant="primary" size="sm" onClick={handleAdd} disabled={createItem.isPending}
                leftIcon={createItem.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              >
                {createItem.isPending ? "Adding…" : "Add first task"}
              </Button>
            </div>
          )}
        </div>
        </>
      )}
    </div>
  );
}

function TaskCard({ item, onToggle, onDelete, onUpdate, togglePending, deletePending }) {
  const completed = !!item.completedAt;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.title);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, [editing]);

  useEffect(() => { setDraft(item.title); }, [item.title]);

  const commit = () => {
    const v = draft.trim();
    if (!v || v === item.title) { setEditing(false); setDraft(item.title); return; }
    onUpdate({ title: v });
    setEditing(false);
  };

  return (
    <div
      style={{
        padding: 14,
        background: "var(--color-bg-surface)",
        border: "1px solid var(--color-border-subtle)",
        borderRadius: 8,
        display: "flex", alignItems: "flex-start", gap: 10,
        position: "relative",
        transition: "border-color 160ms ease",
      }}
      className="crm-onboarding-card"
    >
      <button
        onClick={onToggle}
        disabled={togglePending}
        aria-label={completed ? "Mark as not done" : "Mark as done"}
        style={{
          width: 20, height: 20, padding: 0, flexShrink: 0, marginTop: 1,
          background: "transparent", border: "none",
          color: completed ? "var(--crm-success)" : "var(--color-icon)",
          cursor: togglePending ? "not-allowed" : "pointer",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          transition: "color 120ms",
        }}
      >
        {togglePending ? <Loader2 size={16} className="animate-spin" /> : completed ? <CheckCircle2 size={17} /> : <Circle size={17} />}
      </button>

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
        {editing ? (
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              else if (e.key === "Escape") { setEditing(false); setDraft(item.title); }
            }}
            style={{
              fontSize: 13, fontWeight: 500,
              color: "var(--color-text-primary)",
              background: "transparent",
              border: "none",
              borderBottom: "1px solid var(--crm-primary)",
              padding: "1px 0",
              outline: "none",
              width: "100%",
            }}
          />
        ) : (
          <div
            onClick={() => setEditing(true)}
            style={{
              fontSize: 13, fontWeight: 500, lineHeight: 1.4,
              color: completed ? "var(--color-text-muted)" : "var(--color-text-primary)",
              textDecoration: completed ? "line-through" : "none",
              cursor: "text",
            }}
          >
            {item.title}
          </div>
        )}
        {item.completedAt && (
          <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
            Done {fmtDate(item.completedAt)}
          </div>
        )}
      </div>

      <button
        onClick={onDelete}
        disabled={deletePending}
        className="crm-onboarding-delete"
        aria-label="Delete task"
        style={{
          padding: 4, border: "none", background: "transparent",
          color: "var(--color-icon)",
          opacity: 0, transition: "opacity 120ms, color 120ms",
          cursor: deletePending ? "not-allowed" : "pointer",
          borderRadius: 4, flexShrink: 0,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = "var(--crm-danger)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = "var(--color-icon)"; }}
      >
        {deletePending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
      </button>

      <style>{`
        .crm-onboarding-card:hover .crm-onboarding-delete { opacity: 1; }
      `}</style>
    </div>
  );
}
